// EAR License Exceptions — 15 CFR Part 740 (plus § 754.4(c) for WRC).
// Structured for the /tools/ear-license-exceptions menu + detail pages.
// Each exception has a code, CFR citation, brief description, and optional
// nested `subs` for exceptions like TMP that group several distinct rules
// under one regulatory section.
//
// Descriptions here are compressed summaries — they are NOT the regulatory
// text and should always be verified against the eCFR before use.

export const EAR_EXCEPTION_GROUPS = [
  {
    id: "list-based",
    label: "List-Based Exceptions",
    kicker: "Availability keyed to ECCN language — the ECCN itself must say the exception is available (e.g. \"GBS: Yes\").",
    items: [
      {
        code: "LVS",
        slug: "lvs",
        name: "Limited Value Shipments",
        cfr: "15 CFR § 740.3",
        description: "Small-value commodity shipments to Country Group B destinations. The value cap is the net value of the commodities included in the same order controlled under the same ECCN, up to the LVS dollar limit set inside each eligible ECCN entry.",
      },
      {
        code: "GBS",
        slug: "gbs",
        name: "Shipments to Country Group B",
        cfr: "15 CFR § 740.4",
        description: "Exports and reexports to Country Group B destinations (excluding Sudan and Ukraine) when the Country Chart requires a license only for National Security reasons and the ECCN indicates \"GBS: Yes.\"",
      },
      {
        code: "TSR",
        slug: "tsr",
        name: "Technology and Software Under Restriction",
        cfr: "15 CFR § 740.6",
        description: "Technology or software to destinations where the Country Chart requires a license only for National Security reasons, subject to a written assurance and available only when the ECCN indicates \"TSR: Yes.\"",
      },
      {
        code: "APP",
        slug: "app",
        name: "Computers",
        cfr: "15 CFR § 740.7",
        description: "Computers under ECCN 4A003 (and related software and technology under 4D001 and 4E001) to Computer Tier countries. Eligibility is evaluated against the APP parameter in ECCN 4A003, to the exclusion of other technical parameters.",
      },
      {
        code: "NAC / ACA",
        slug: "nac-aca",
        name: "Notified Advanced Computing / Advanced Computing Authorized",
        cfr: "15 CFR § 740.8",
        description: "Two paired exceptions for advanced computing ICs and related items — NAC covers Macau, Country Group D:5, and entities headquartered there; ACA covers every other destination. Excludes 3A090.c.",
      },
      {
        code: "ENC",
        slug: "enc",
        name: "Encryption Commodities, Software, and Technology",
        cfr: "15 CFR § 740.17",
        description: "Encryption commodities, software, and technology under ECCNs 5A002, 5B002, 5D002, and 5E002, plus cryptanalytic and digital forensics items under 5A004. Not available for Country Group E:1 or E:2.",
      },
      {
        code: "STA",
        slug: "sta",
        name: "Strategic Trade Authorization",
        cfr: "15 CFR § 740.20",
        description: "Exports, reexports, and in-country transfers to destinations in Country Group A:5 or A:6, when the ECCN's \"License Exceptions\" paragraph does not prohibit STA and all consignee-statement, prior-consignee, and reporting conditions are satisfied.",
      },
      {
        code: "ACE",
        slug: "ace",
        name: "Authorized Cybersecurity Exports",
        cfr: "15 CFR § 740.22",
        description: "\"Cybersecurity items\" at ECCNs 4A005, 4D001.a, 4D004, 4E001.a, 4E001.c, 5A001.j, 5B001.a, 5D001.a, 5D001.c, and 5E001.a — subject to end-user and end-use restrictions in § 740.22(c).",
      },
      {
        code: "IEC",
        slug: "iec",
        name: "Implemented Export Control",
        cfr: "15 CFR § 740.24",
        description: "Items and destinations identified in the \"License Exception IEC Eligible Items and Destinations\" document — incorporated by reference into § 740.24 and posted at bis.gov/IEC.",
      },
      {
        code: "HBM",
        slug: "hbm",
        name: "High Bandwidth Memory",
        cfr: "15 CFR § 740.25",
        description: "High bandwidth memory at ECCN 3A090.c, subject to headquarters, co-packaging, and reporting conditions.",
      },
      {
        code: "RFF",
        slug: "rff",
        name: "Restricted Fabrication \"Facility\"",
        cfr: "15 CFR § 740.26",
        description: "Items to a \"restricted fabrication facility\" — an Entity List entity flagged with a § 740.26 reference. Items may not be used to produce advanced-node integrated circuits.",
      },
      {
        code: "AIA",
        slug: "aia",
        name: "Artificial Intelligence Authorization",
        cfr: "15 CFR § 740.27",
        description: "Eligible advanced computing ICs, related software and technology, and AI model weights at ECCN 4E091, to entities headquartered (and with an ultimate parent headquartered) in an AI Authorization Country.",
      },
      {
        code: "ACM",
        slug: "acm",
        name: "Advanced Compute Manufacturing",
        cfr: "15 CFR § 740.28",
        description: "Eligible advanced computing items to a \"private sector end user\" for development, production, or storage, where the item is ultimately destined outside Macau or Country Group D:5.",
      },
      {
        code: "LPP",
        slug: "lpp",
        name: "Low Processing Performance",
        cfr: "15 CFR § 740.29",
        description: "Up to 26,900,000 cumulative Total Processing Performance (TPP) of specified advanced computing ICs per calendar year to a single ultimate consignee.",
      },
    ],
  },
  {
    id: "non-list-based",
    label: "Non-List-Based Exceptions",
    kicker: "Availability turns on transaction type — the ECCN does not need to enumerate the exception.",
    items: [
      {
        code: "TMP",
        slug: "tmp",
        name: "Temporary Imports, Exports, Reexports, and Transfers (In-Country)",
        cfr: "15 CFR § 740.9",
        description: "A single regulatory section housing ten distinct temporary-movement rules — each with its own eligibility criteria, return conditions, and destination limits.",
        subs: [
          {
            code: "TMP · Tools of the Trade",
            slug: "tmp-tools-of-the-trade",
            name: "Tools of the Trade",
            cfr: "15 CFR § 740.9(a)(1)",
            description: "Temporary exports, reexports, and in-country transfers of commodities and software as tools of the trade for use by the exporter or its employees.",
          },
          {
            code: "TMP · Traveling with Technology",
            slug: "tmp-traveling-with-technology",
            name: "Traveling with Technology",
            cfr: "15 CFR § 740.9(a)(3)",
            description: "Temporary exports, reexports, and in-country transfers of technology by or to a U.S. person, or a foreign-person employee of a U.S. person, traveling or on temporary assignment abroad.",
          },
          {
            code: "TMP · Kits of Replacement Parts",
            slug: "tmp-kits-of-replacement-parts",
            name: "Kits of Replacement Parts",
            cfr: "15 CFR § 740.9(a)(4)",
            description: "Temporary movement of kits consisting of replacement parts or components — kits must be returned except for 1-for-1 replacements qualifying under License Exception RPL.",
          },
          {
            code: "TMP · Exhibition and Demonstration",
            slug: "tmp-exhibition-demonstration",
            name: "Exhibition and Demonstration",
            cfr: "15 CFR § 740.9(a)(5)",
            description: "Temporary movement of commodities and software for exhibition or demonstration.",
          },
          {
            code: "TMP · Inspection, Test, Calibration & Repair",
            slug: "tmp-inspection-test-calibration-repair",
            name: "Inspection, Test, Calibration & Repair",
            cfr: "15 CFR § 740.9(a)(6)",
            description: "Temporary movement of commodities to be inspected, tested, calibrated, or repaired abroad.",
          },
          {
            code: "TMP · Containers",
            slug: "tmp-containers",
            name: "Containers",
            cfr: "15 CFR § 740.9(a)(7)",
            description: "Temporary movement of containers necessary for shipment of otherwise-authorized commodities, when another license exception is not available.",
          },
          {
            code: "TMP · Assembly in Mexico",
            slug: "tmp-assembly-in-mexico",
            name: "Assembly in Mexico",
            cfr: "15 CFR § 740.9(a)(8)",
            description: "Temporary movement of commodities to Mexico under Customs entries requiring return to the U.S. after processing, assembly, or incorporation by companies participating in Mexico's IMMEX program.",
          },
          {
            code: "TMP · News Media",
            slug: "tmp-news-media",
            name: "News Media",
            cfr: "15 CFR § 740.9(a)(9)",
            description: "Temporary movement of commodities (and enabling software) for accredited news-gathering personnel to or within Belarus, Cuba, North Korea, Russia, Syria, the Crimea region of Ukraine, and covered regions of Ukraine.",
          },
          {
            code: "TMP · Foreign Subsidiary / Affiliate / Facility",
            slug: "tmp-foreign-subsidiary",
            name: "Foreign Subsidiary, Affiliate, or Facility",
            cfr: "15 CFR § 740.9(a)(10)",
            description: "Temporary exports of components, parts, tools, accessories, or test equipment by a U.S. person to a subsidiary, affiliate, or facility it owns or controls, to manufacture, assemble, test, produce, or modify items.",
          },
          {
            code: "TMP · Body Armor & Protective Equipment",
            slug: "tmp-body-armor",
            name: "Body Armor and Other Protective Equipment",
            cfr: "15 CFR § 740.9(a)(11)",
            description: "Temporary movement of items at ECCN 1A613.c and .d and 1A607.f.",
          },
        ],
      },
      {
        code: "RPL",
        slug: "rpl",
        name: "Servicing and Replacement of Parts and Equipment",
        cfr: "15 CFR § 740.10",
        description: "One-for-one replacement of previously exported parts, components, accessories, and attachments — plus a separate rule for servicing and export/reexport to Country Group D:5.",
        subs: [
          {
            code: "RPL · Replacement of Parts",
            slug: "rpl-replacement-of-parts",
            name: "Replacement of Parts, Components, Accessories, and Attachments",
            cfr: "15 CFR § 740.10(a)",
            description: "Exports and reexports of 1-for-1 replacement parts, components, accessories, and attachments that were previously exported.",
          },
          {
            code: "RPL · Servicing and Replacement",
            slug: "rpl-servicing-d5",
            name: "Servicing and Replacement",
            cfr: "15 CFR § 740.10(b)",
            description: "Export and reexport of commodities and software sent to the U.S. or a foreign party for servicing, plus replacements for defective or unacceptable EAR items. Not available for 9x515 or \"600 series\" items to Country Group D:5, and no repaired item may go to Country Group E:1.",
          },
        ],
      },
      {
        code: "GOV",
        slug: "gov",
        name: "Governments, International Organizations, CWC Inspections, and the ISS",
        cfr: "15 CFR § 740.11",
        description: "Five distinct pathways for shipments involving government or intergovernmental actors, from IAEA safeguards to the International Space Station.",
        subs: [
          {
            code: "GOV · International Safeguards",
            slug: "gov-international-safeguards",
            name: "International Safeguards (IAEA / Euratom)",
            cfr: "15 CFR § 740.11(a)",
            description: "Exports and reexports of commodities or software to the IAEA and Euratom, and reexports by IAEA and Euratom.",
          },
          {
            code: "GOV · U.S. Government",
            slug: "gov-us-government",
            name: "U.S. Government",
            cfr: "15 CFR § 740.11(b)",
            description: "Exports, reexports, and in-country transfers to the U.S. Government and exports by or for DOD or DOE.",
          },
          {
            code: "GOV · Cooperating Governments & NATO",
            slug: "gov-cooperating-and-nato",
            name: "Cooperating Governments and NATO",
            cfr: "15 CFR § 740.11(c)",
            description: "Exports, reexports, and in-country transfers to government agencies of NATO countries, Country Group A:1 countries, Singapore, or Taiwan.",
          },
          {
            code: "GOV · CWC Inspections",
            slug: "gov-cwc-inspections",
            name: "International Inspections Under the Chemical Weapons Convention",
            cfr: "15 CFR § 740.11(d)",
            description: "Exports and reexports to the Organization for the Prohibition of Chemical Weapons (OPCW), and exports and reexports by OPCW.",
          },
          {
            code: "GOV · International Space Station",
            slug: "gov-international-space-station",
            name: "International Space Station",
            cfr: "15 CFR § 740.11(e)",
            description: "Exports and reexports of items at ECCN 9A004 to launch sites for supply missions to the ISS.",
          },
        ],
      },
      {
        code: "GFT",
        slug: "gft",
        name: "Gift Parcels and Humanitarian Donations",
        cfr: "15 CFR § 740.12",
        description: "Gift parcels from an individual donor to an individual or a religious, charitable, or educational organization; plus humanitarian donations of eligible items to meet basic human needs.",
      },
      {
        code: "TSU",
        slug: "tsu",
        name: "Technology and Software — Unrestricted",
        cfr: "15 CFR § 740.13",
        description: "Six categories of technology and software transfers that ride alongside — or clean up after — a lawful export of an underlying item.",
        subs: [
          {
            code: "TSU · Operation Technology & Software",
            slug: "tsu-operation-tech-and-software",
            name: "Operation Technology and Software",
            cfr: "15 CFR § 740.13(a)",
            description: "Exports and reexports of operation technology and software for items that are lawfully exported or reexported under separate authority.",
          },
          {
            code: "TSU · Sales Technology",
            slug: "tsu-sales-technology",
            name: "Sales Technology",
            cfr: "15 CFR § 740.13(b)",
            description: "Data supporting a prospective or actual quotation, bid, offer to sell, lease, or supply any item.",
          },
          {
            code: "TSU · Software Updates",
            slug: "tsu-software-updates",
            name: "Software Updates",
            cfr: "15 CFR § 740.13(c)",
            description: "Software updates that fix bugs in software lawfully exported or reexported under separate authority.",
          },
          {
            code: "TSU · Mass Market Software",
            slug: "tsu-mass-market-software",
            name: "Mass Market Software",
            cfr: "15 CFR § 740.13(d)",
            description: "Exports and reexports of mass market software satisfying the § 740.17(b)(1) or Note 3 to Category 5 — Part 2 criteria.",
          },
          {
            code: "TSU · Deemed Exports by Universities",
            slug: "tsu-deemed-exports-universities",
            name: "Deemed Exports by U.S. Universities to Foreign Employees",
            cfr: "15 CFR § 740.13(f)",
            description: "Release in the United States of technology and source code by U.S. universities to foreign nationals who are their bona fide, full-time regular employees.",
          },
          {
            code: "TSU · Copies of Technology",
            slug: "tsu-copies-of-technology",
            name: "Copies of Technology Previously Authorized",
            cfr: "15 CFR § 740.13(g)",
            description: "Exports, reexports, and in-country transfers of copies of technology previously authorized under separate authority to the same recipient.",
          },
        ],
      },
      {
        code: "BAG",
        slug: "bag",
        name: "Baggage",
        cfr: "15 CFR § 740.14",
        description: "Exports and reexports of personal-use items in personal baggage.",
      },
      {
        code: "AVS",
        slug: "avs",
        name: "Aircraft, Vessels, and Spacecraft",
        cfr: "15 CFR § 740.15",
        description: "Departure of U.S. and foreign-registry civil aircraft on temporary sojourn; equipment and spare parts for permanent use on a vessel or aircraft; exports to U.S., Australian, Canadian, or UK vessels, planes, airlines' installations, or agents; cargo transiting Cuba on an aircraft or vessel on temporary sojourn; and export of spacecraft and components for fundamental research.",
      },
      {
        code: "APR",
        slug: "apr",
        name: "Additional Permissive Reexports",
        cfr: "15 CFR § 740.16",
        description: "Four reexport pathways from and among close-ally jurisdictions.",
        subs: [
          {
            code: "APR · From A:1",
            slug: "apr-from-a1",
            name: "Reexports From Country Group A:1",
            cfr: "15 CFR § 740.16(a)",
            description: "Reexports from countries in Country Group A:1 under an export authorization from the government of the reexporting country.",
          },
          {
            code: "APR · To/Among A:1",
            slug: "apr-to-among-a1",
            name: "Reexports To and Among Country Group A:1",
            cfr: "15 CFR § 740.16(b)",
            description: "Reexports to and among destinations in Country Group A:1 for use or consumption within a destination in Country Group A:1.",
          },
          {
            code: "APR · From Australia, Canada, or UK",
            slug: "apr-from-australia-canada-uk",
            name: "Reexports From Australia, Canada, or the United Kingdom",
            cfr: "15 CFR § 740.16(d)",
            description: "Reexports from Australia, Canada, or the UK where a license exception otherwise would have been available to export the same item directly from the U.S. to that destination.",
          },
          {
            code: "APR · NP Column 1 to A:4",
            slug: "apr-np-column-1-to-a4",
            name: "NP Column 1 Items to Country Group A:4",
            cfr: "15 CFR § 740.16(j)",
            description: "Reexports of items controlled by NP Column 1 to, among, and from countries in Country Group A:4.",
          },
        ],
      },
      {
        code: "AGR",
        slug: "agr",
        name: "Agricultural Commodities",
        cfr: "15 CFR § 740.18",
        description: "Export and reexport of agricultural commodities to Cuba.",
      },
      {
        code: "CCD",
        slug: "ccd",
        name: "Consumer Communications Devices",
        cfr: "15 CFR § 740.19",
        description: "Export, reexport, and in-country transfer to Belarus, Cuba, Russia, and Syria of consumer computers, tablets, mobile phones, consumer software, and other similar items.",
      },
      {
        code: "SCP",
        slug: "scp",
        name: "Support for the Cuban People",
        cfr: "15 CFR § 740.21",
        description: "Export and reexport to Cuba of items designated as EAR99 or controlled only for anti-terrorism reasons, or as authorized by OFAC.",
      },
      {
        code: "MED",
        slug: "med",
        name: "Medical Devices",
        cfr: "15 CFR § 740.23",
        description: "Export, reexport, and in-country transfer of EAR99 medical devices to or within Russia, Belarus, the temporarily occupied Crimea region of Ukraine, or the covered regions of Ukraine.",
      },
      {
        code: "WRC",
        slug: "wrc",
        name: "Western Red Cedar",
        cfr: "15 CFR § 754.4(c)",
        description: "Export of unprocessed western red cedar timber at ECCN 1C988 harvested from Federal, State, and other public lands in Alaska; private lands; or lands held in trust for recognized Indian tribes.",
      },
    ],
  },
];

// Build a flat map { slug: {…item, group, parent?} } for O(1) lookups on the
// detail page. Sub-exceptions inherit their parent's group + carry a parent
// reference so we can render breadcrumbs like "TMP → Tools of the Trade."
export function buildEarExceptionIndex() {
  const idx = {};
  for (const group of EAR_EXCEPTION_GROUPS) {
    for (const item of group.items) {
      idx[item.slug] = { ...item, group };
      if (item.subs) {
        for (const sub of item.subs) {
          idx[sub.slug] = { ...sub, group, parent: item };
        }
      }
    }
  }
  return idx;
}
